const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

export class ApiService {
  private static instance: ApiService;
  private baseURL: string;

  private constructor() {
    this.baseURL = API_BASE_URL;
  }

  public static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService();
    }
    return ApiService.instance;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    
    const defaultHeaders: { [key: string]: string } = {
      'Content-Type': 'application/json',
    };

    const token = localStorage.getItem('auth_token');
    if (token && !endpoint.includes('/admin/authorize')) {
      defaultHeaders['Authorization'] = `Bearer ${token}`;
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        if (response.status === 401) {
          // Import here to avoid circular dependency
          const { tokenManager } = await import('./tokenManager');
          const shouldRetry = await tokenManager.handleApiError(response.status);
          
          if (shouldRetry) {
            // Update token in headers and retry
            const newToken = localStorage.getItem('auth_token');
            if (newToken) {
              config.headers = {
                ...config.headers,
                'Authorization': `Bearer ${newToken}`
              } as { [key: string]: string };
              
              const retryResponse = await fetch(url, config);
              if (!retryResponse.ok) {
                const errorText = await retryResponse.text();
                throw new Error(errorText || `HTTP error! status: ${retryResponse.status}`);
              }

              // Handle empty responses for retry
              const retryContentType = retryResponse.headers.get('content-type');
              const retryContentLength = retryResponse.headers.get('content-length');

              if (retryContentLength === '0' || retryResponse.status === 204) {
                return {} as T;
              }

              if (retryContentType && retryContentType.includes('application/json')) {
                const text = await retryResponse.text();
                return text ? JSON.parse(text) : {} as T;
              }

              return {} as T;
            }
          }
        }
        
        const errorText = await response.text();
        throw new Error(errorText || `HTTP error! status: ${response.status}`);
      }

      // Check if response has content before parsing JSON
      const contentType = response.headers.get('content-type');
      const contentLength = response.headers.get('content-length');

      // If no content or empty response, return empty object
      if (contentLength === '0' || response.status === 204) {
        return {} as T;
      }

      // Only parse JSON if content-type indicates JSON
      if (contentType && contentType.includes('application/json')) {
        const text = await response.text();
        return text ? JSON.parse(text) : {} as T;
      }

      // For non-JSON responses, return empty object
      return {} as T;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  public async post<T>(endpoint: string, data: any, contentType: string = 'application/json'): Promise<T> {
    const headers: { [key: string]: string } = {};

    if (contentType === 'application/x-www-form-urlencoded') {
      headers['Content-Type'] = 'application/x-www-form-urlencoded';
    }

    return this.makeRequest<T>(endpoint, {
      method: 'POST',
      body: contentType === 'application/x-www-form-urlencoded' ? data : JSON.stringify(data),
      headers
    });
  }

  public async get<T>(endpoint: string): Promise<T> {
    return this.makeRequest<T>(endpoint, {
      method: 'GET',
    });
  }

  public async put<T>(endpoint: string, data: any): Promise<T> {
    return this.makeRequest<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  public async delete<T>(endpoint: string): Promise<T> {
    return this.makeRequest<T>(endpoint, {
      method: 'DELETE',
    });
  }

  public async patch<T>(endpoint: string, data: any): Promise<T> {
    return this.makeRequest<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  public async uploadFile<T>(endpoint: string, file: File): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('auth_token');
    const headers: { [key: string]: string } = {};

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
      });

      if (!response.ok) {
        if (response.status === 401) {
          const { tokenManager } = await import('./tokenManager');
          const shouldRetry = await tokenManager.handleApiError(response.status);

          if (shouldRetry) {
            const newToken = localStorage.getItem('auth_token');
            if (newToken) {
              headers['Authorization'] = `Bearer ${newToken}`;

              const retryResponse = await fetch(url, {
                method: 'POST',
                headers,
                body: formData,
              });

              if (!retryResponse.ok) {
                throw new Error(`HTTP error! status: ${retryResponse.status}`);
              }

              return await retryResponse.json();
            }
          }
        }

        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('File upload failed:', error);
      throw error;
    }
  }
}